import React, { useRef, FC, DragEvent, ChangeEvent, useState } from "react";
import { infoIcon, uploadBlackIcon, uploadIcon } from "utils/constant";

interface FileUploadProps {
  label?: string;
  fileTypes?: string;
  required?: boolean;
  info?: string;
  onChange: (file: File | null) => void;
}

const FileUpload: FC<FileUploadProps> = ({
  label = "Attachment",
  fileTypes = "*",
  required = false,
  info = "",
  onChange,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0] ?? null;
    setSelectedFile(file);
    onChange(file);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
    onChange(file);
  };

  const clearFile = () => {
    setSelectedFile(null);
    onChange(null);
    // also clear the input value so the same file can be re‑selected if needed
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="flex flex-col w-full">
      {/* Label */}
      <label
        htmlFor="file-upload"
        className="flex flex-row items-center text-gray-700 font-medium mb-2 gap-2"
      >
        {infoIcon}
        {label}
        {required && (
          <span className="text-red-500" title="This field is required">
            *
          </span>
        )}
      </label>

      {/* If a file is selected, show the chip */}
      {selectedFile ? (
        <div className="flex items-center justify-between rounded-md border border-gray-300 bg-gray-50 px-4 py-2">
          <span className="truncate">{selectedFile.name}</span>
          <button
            type="button"
            onClick={clearFile}
            className="ml-4 text-gray-400 hover:text-gray-600"
            aria-label="Remove selected file"
          >
            ×
          </button>
        </div>
      ) : (
        /* Dropzone */
        <div
          className="
            group relative flex cursor-pointer flex-col items-center justify-center
            rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 py-12
            hover:bg-gray-100 transition
          "
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
        >
          {uploadBlackIcon}
          <p className="text-gray-500 group-hover:text-gray-600">
            Upload a file here
          </p>
        </div>
      )}

      {/* Hidden file input */}
      <input
        id="file-upload"
        type="file"
        accept={fileTypes}
        ref={inputRef}
        className="hidden"
        onChange={handleChange}
      />

      {/* Hint */}
      {info && <p className="mt-1 text-xs text-gray-500">{info}</p>}
    </div>
  );
};

export default FileUpload;
